/**
 * Pure formatting for the admin dashboard: relative times, the charts' bars
 * and lines, the market's movers, and the tab list. No React, so it is
 * tested in milliseconds.
 */
import { GAME_TYPES, type GameType } from '@tv-casino/shared';
import {
  KIND_LABELS,
  highlightsOf,
  maxDrawdown,
  sparkLegendOf,
  sparklineOf,
  sparkMarksOf,
  sparkRunsOf,
  type MarketHighlight,
  type SparkLegendItem,
  type SparkMark,
  type SparkPoint,
  type SparkRun,
} from '../components/memberMarketModel.js';
import type { AdminDay, AdminMarketFlow, AdminMarketMover } from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';

export type AdminTab = 'overview' | 'market' | 'players' | 'rounds' | 'users' | 'casinos' | 'live';

export const ADMIN_TABS: readonly { id: AdminTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'market', label: 'Market' },
  { id: 'players', label: 'Players' },
  { id: 'rounds', label: 'Game history' },
  { id: 'users', label: 'Users' },
  { id: 'casinos', label: 'Casinos' },
  { id: 'live', label: 'Live now' },
];

export const parseTab = (raw: string | null): AdminTab =>
  ADMIN_TABS.some((t) => t.id === raw) ? (raw as AdminTab) : 'overview';

/** "just now", "5m ago", "3h ago", "2d ago", then a date. "never" for null. */
export function timeAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'never';
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(then).toISOString().slice(0, 10);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-09-14` → "Sep 14". */
export function dayLabel(day: string): string {
  const [, month, date] = day.split('-');
  return `${MONTHS[Number(month) - 1] ?? month} ${Number(date)}`;
}

export interface ActivitySegment {
  game: GameType;
  hands: number;
}

export interface ActivityBar {
  day: string;
  /** Short axis label, e.g. "Sep 14". */
  label: string;
  hands: number;
  players: number;
  /** Bar height as a share of the busiest day, 0–1. */
  share: number;
  /** The day's hands split by game, in the fixed game order, bottom first. */
  segments: ActivitySegment[];
}

export function activityBars(days: AdminDay[]): {
  bars: ActivityBar[];
  peak: number;
  total: number;
  /** Games that stacked into at least one bar, for the legend. */
  games: GameType[];
} {
  const peak = Math.max(0, ...days.map((d) => d.hands));
  const bars = days.map((d) => ({
    day: d.day,
    label: dayLabel(d.day),
    hands: d.hands,
    players: d.players,
    share: peak === 0 ? 0 : d.hands / peak,
    segments: GAME_TYPES.filter((g) => (d.games[g] ?? 0) > 0).map((game) => ({
      game,
      hands: d.games[game]!,
    })),
  }));
  const games = GAME_TYPES.filter((g) => bars.some((b) => b.segments.some((s) => s.game === g)));
  return { bars, peak, total: days.reduce((n, d) => n + d.hands, 0), games };
}

/**
 * Each value's height between the series' low (0) and high (1). A flat
 * series sits on 0.5 rather than hugging either edge.
 */
export function lineHeights(values: readonly number[]): number[] {
  if (values.length === 0) return [];
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return values.map((v) => (hi > lo ? (v - lo) / (hi - lo) : 0.5));
}

/** Running totals, so a daily result reads as a cumulative line. */
export function cumulative(values: readonly number[]): number[] {
  let sum = 0;
  return values.map((v) => (sum += v));
}

export interface HouseBar {
  day: string;
  label: string;
  /** The house side of the day: players down is the house up. */
  house: number;
  /** Signed share of the day with the largest swing either way, −1..1. */
  share: number;
}

/** One diverging bar per day, scaled to the biggest swing in the window. */
export function houseBars(days: readonly { day: string; playersNet: number }[]): {
  bars: HouseBar[];
  total: number;
} {
  const scale = Math.max(0, ...days.map((d) => Math.abs(d.playersNet)));
  const bars = days.map((d) => {
    const house = 0 - d.playersNet;
    return { day: d.day, label: dayLabel(d.day), house, share: scale === 0 ? 0 : house / scale };
  });
  return { bars, total: bars.reduce((n, b) => n + b.house, 0) };
}

/** Players' combined result, read from the house side: players down = house up. */
export function houseResult(playersNet: number): { label: string; tone: 'up' | 'down' | 'flat' } {
  const house = -playersNet;
  const abs = Math.abs(house).toLocaleString('en-US');
  if (house > 0) return { label: `House +${abs}`, tone: 'up' };
  if (house < 0) return { label: `House −${abs}`, tone: 'down' };
  return { label: 'House even', tone: 'flat' };
}

/** "+4.2%", "−10.0%", "0.0%"; "—" with nothing to divide by. */
export function signedPct(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const abs = Math.abs(pct).toFixed(1);
  if (pct > 0) return `+${abs}%`;
  if (pct < 0) return `−${abs}%`;
  return `${abs}%`;
}

/** What the house kept of every chip wagered at a game. */
export function houseHold(playersNet: number, wagered: number): { label: string; tone: 'up' | 'down' | 'flat' } {
  if (wagered <= 0) return { label: '—', tone: 'flat' };
  const hold = (0 - playersNet) / wagered;
  return { label: signedPct(hold), tone: hold > 0 ? 'up' : hold < 0 ? 'down' : 'flat' };
}

export interface FlowRow {
  kind: string;
  label: string;
  count: number;
  total: number;
  totalLabel: string;
  /** |total| against the biggest flow, 0–1. */
  share: number;
}

/** Ledger kinds as signed money flow, biggest first as the server sends them. */
export function flowRows(flows: readonly AdminMarketFlow[]): FlowRow[] {
  const biggest = Math.max(0, ...flows.map((f) => Math.abs(f.total)));
  return flows.map((f) => ({
    kind: f.kind,
    label: KIND_LABELS[f.kind] ?? f.kind,
    count: f.count,
    total: f.total,
    totalLabel: formatSignedChips(f.total),
    share: biggest === 0 ? 0 : Math.abs(f.total) / biggest,
  }));
}

/** Up / even / down as shares of every holder, for one stacked bar. */
export function breadthShares(b: { up: number; down: number; flat: number }): {
  up: number;
  flat: number;
  down: number;
  total: number;
} {
  const total = b.up + b.down + b.flat;
  if (total === 0) return { up: 0, flat: 0, down: 0, total };
  return { up: b.up / total, flat: b.flat / total, down: b.down / total, total };
}

export interface MoverVM {
  key: string;
  name: string;
  casinoName: string;
  chipsLabel: string;
  changeLabel: string;
  /** Change against what the house staked. */
  changePctLabel: string;
  tone: 'up' | 'down' | 'flat';
  volumeLabel: string;
  drawdownLabel: string;
  /** The window's story: best hand, streak, table of choice. */
  highlights: MarketHighlight[];
  spark: SparkPoint[];
  sparkRuns: SparkRun[];
  sparkMarks: SparkMark[];
  sparkSingle: boolean;
  sparkLegend: SparkLegendItem[];
}

/** One mover's quote and balance line, in the member desk's own vocabulary. */
export function moverModel(m: AdminMarketMover): MoverVM {
  const spark = sparklineOf(m.series);
  const runs = sparkRunsOf(m.series, spark.points);
  const dd = maxDrawdown(m.series.points);
  return {
    key: `${m.casinoId}:${m.memberId}`,
    name: m.name,
    casinoName: m.casinoName,
    chipsLabel: m.chips.toLocaleString('en-US'),
    changeLabel: formatSignedChips(m.change),
    changePctLabel: signedPct(m.buyIn > 0 ? m.change / m.buyIn : null),
    tone: m.change > 0 ? 'up' : m.change < 0 ? 'down' : 'flat',
    volumeLabel: m.volume.toLocaleString('en-US'),
    drawdownLabel: dd === null || dd === 0 ? '—' : signedPct(-dd),
    highlights: highlightsOf(m.totals, m.favoriteGame),
    spark: spark.points,
    sparkRuns: runs,
    sparkMarks: sparkMarksOf(m.series, spark.points),
    sparkSingle: spark.single,
    sparkLegend: sparkLegendOf(runs),
  };
}
